// @flow
import './polyfills';
import React from 'react';
import { render } from 'react-dom';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';
import {
  createInstance,
  OptimizelyProvider,
  setLogLevel,
  setLogger,
} from '@optimizely/react-sdk';

import { getAccount } from 'services/api/account';
import {
  receiveUserStatus,
  receiveUserError,
} from 'services/actions/userActions';
import env from 'utils/env';
import { initializeDatadogRum, initializeDatadogLogs } from 'utils/datadog';
import store from './store';

import ErrorBoundary from './shared-components/ErrorBoundary/ErrorBoundary';
import RouteProvider from './routes/RouteProvider';
import * as serviceWorker from './serviceWorker';

import ErrorPage from './Scenes/ErrorPages/ErrorPage';

import customLogger from './utils/customOptimizelyLogger';

const root = document.querySelector('#root');
const optimizelyClientInstance = createInstance({
  sdkKey: env.optimizely,
  autoUpdate: true,
  updateInterval: 300000, // 5 minutes in milliseconds
});

const selectTestUserId = '975160041';
const nonSelectTestUserId = '975159869';

optimizelyClientInstance.onReady().then(() => {
  window.optimizelyClientInstance = optimizelyClientInstance;
  // set forcedVariation for test user to assign a specific variation for p2p
  optimizelyClientInstance.setForcedVariation(
    'ecomm-product-accordion-recs-1_0',
    selectTestUserId,
    'similar_recs'
  );
  optimizelyClientInstance.setForcedVariation(
    'ecomm-main_page_product_sort_1_0',
    selectTestUserId,
    'best_selling'
  );
  optimizelyClientInstance.setForcedVariation(
    'ecomm-main_page_product_sort_1_0',
    nonSelectTestUserId,
    'best_selling'
  );
});

if (env.appEnv === 'prod') {
  setLogLevel();

  setLogger({
    log: customLogger,
  });
} else {
  setLogLevel('warning');

  setLogger({
    log: customLogger,
  });
}

initializeDatadogRum();
initializeDatadogLogs();

if (root !== null) {
  // see https://docs.launchdarkly.com/sdk/client-side/react#asyncwithldprovider
  (async () => {
    let ldArgs;
    let id;

    try {
      const accountResponse = await getAccount();
      store.dispatch(receiveUserStatus(accountResponse));
      const { userId } = accountResponse;
      id = userId.toString();

      ldArgs = {
        clientSideID: env.launchDarkly,
        context: {
          kind: 'user',
          key: id,
        },
        options: {
          useReport: true,
        },
      };
    } catch {
      ldArgs = { clientSideID: env.launchDarkly };
      store.dispatch(receiveUserError());
      id = 'anonymous';
    }

    const LDProvider = await asyncWithLDProvider(ldArgs);

    render(
      <LDProvider>
        <OptimizelyProvider optimizely={optimizelyClientInstance} user={{ id }}>
          <Provider store={store}>
            <BrowserRouter>
              <ErrorBoundary fallbackUI={ErrorPage}>
                <RouteProvider />
              </ErrorBoundary>
            </BrowserRouter>
          </Provider>
        </OptimizelyProvider>
      </LDProvider>,
      root
    );
  })();
}

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: http://bit.ly/CRA-PWA
serviceWorker.unregister();
